import React, { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import EntityTable from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTable';
import { TopicFilter } from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/Filters/constants';
import {
  useTopicBuilderActions,
  useTopicBuilderFilters
} from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/Store/useTopicBuilderStore';
import useTopicAdvancedRequestBuilder from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/hooks/useTopicAdvancedRequestBuilder';
import { topicSettingsServerProxy } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Settings/serverProxy';
import { TOPIC_QUERY_KEYS } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Settings/topicQueryKeys';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import MetricCell from 'src/components/BeaconV2Components/MetricCell/MetricCell';
import { FETCH_CHUNK_SIZE } from 'src/components/BeaconV2Components/MetricList/MetricListContainer';
import { TableSpan } from 'src/components/BeaconV2Components/MetricList/headColumnDefinitions';
import Pagination from 'src/components/BeaconV2Components/Pagination/Pagination';
import { GRID_ITEMS_PER_PAGE } from 'src/components/BeaconV2Components/ProductGrid/ProductGrid';
import { SlCancelButton } from 'src/components/BeaconV2Components/SlButtons/SlCancelButton';
import { useTableData } from 'src/serverProxy/useTableData';
import { useMainEntity } from 'src/utils/Hooks/reduxSelectors';
import { METRICTYPE } from 'src/utils/entityDefinitions';

const fields = [
  {
    name: 'totalClicks',
    displayName: 'Search Volume'
  }
];

interface UseEditTopicResultsParams {
  shouldFetch: boolean;
  page: number;
}

const useEditTopicResults = ({ shouldFetch, page }: UseEditTopicResultsParams) => {
  const PAGES_PER_CHUNK = Math.ceil(FETCH_CHUNK_SIZE / GRID_ITEMS_PER_PAGE);
  const keywordSearchRequestBuilder = useTopicAdvancedRequestBuilder('retailSales');

  const { isLoading: tableDataLoading, data: tableData } = useTableData<{ metadata: any }>({
    fields: [...fields],
    groupByFieldName: 'searchTerm',
    indexName: 'traffic',
    requestId: 'topic-builder-search-results',
    itemsPerPage: FETCH_CHUNK_SIZE,
    pageNumber: Math.ceil(page / PAGES_PER_CHUNK),
    sortFieldName: 'totalClicks',
    useGenericAdvancedSearchArgs: {
      shouldPerformFetch: shouldFetch
    },
    buildRequest: keywordSearchRequestBuilder()
  });

  const { data: results, isLoading: resultsLoading } = useQuery({
    enabled: !tableDataLoading,
    queryKey: [TOPIC_QUERY_KEYS.all, 'search-term-classification', tableData],
    queryFn: async () => {
      const searchTermData = (tableData ?? [])?.map((datum) => ({
        searchTerm: datum.metadata?.searchTerm,
        totalClicks: datum?.totalClicks_sum_value ?? 0
      }));

      const searchTermClassificationResponse = searchTermData?.length
        ? await topicSettingsServerProxy.getSearchTermClassification({
            searchTermList: searchTermData?.map(({ searchTerm }) => searchTerm)
          })
        : { records: [] };

      const classificationMap = searchTermClassificationResponse?.records.reduce((acc, { searchTerm, brand, type }) => {
        acc[searchTerm] = { brand, type };
        return acc;
      }, {});

      return searchTermData.reduce((acc, { searchTerm, totalClicks }) => {
        acc = [
          ...acc,
          {
            searchTerm,
            totalClicks,
            ...classificationMap[searchTerm]
          }
        ];

        return acc;
      }, []) as ClassifiedSearchTerm[];
    }
  });

  return { data: results, isLoading: resultsLoading || tableDataLoading };
};

interface ClassifiedSearchTerm {
  searchTerm: string;
  totalClicks: number;
  brand: string;
  type: string;
}

/**
 * Renders a table containing keywords and their metrics and additional visualizations that are filtered via sidebar.
 */
const PAGES_PER_CHUNK = FETCH_CHUNK_SIZE / GRID_ITEMS_PER_PAGE;

export const TopicBuilderEditResults = ({ readOnly }: { readOnly: boolean }) => {
  const entity = useMainEntity();
  const { setFilters } = useTopicBuilderActions();
  const { filters, ready } = useTopicBuilderFilters();

  const [hoveredIndex, setHoveredIndex] = React.useState(null);

  const [page, setPage] = React.useState(1);
  const [dataByPageNumber, setDataByPageNumber] = React.useState<{ [key: number]: ClassifiedSearchTerm[] }>({});

  const { data: results, isLoading } = useEditTopicResults({ shouldFetch: ready, page });

  useEffect(() => {
    setDataByPageNumber((oldData) => ({
      ...oldData,
      [Math.ceil(page / PAGES_PER_CHUNK)]: results
    }));
  }, [results, page]);

  useEffect(() => {
    setPage(1);
  }, [filters]);

  const handlePageNext = () => {
    const adjustedPage = ((page - 1) % PAGES_PER_CHUNK) + 1;
    const startIndex = (adjustedPage - 1) * GRID_ITEMS_PER_PAGE;
    const endIndex = startIndex + GRID_ITEMS_PER_PAGE;

    if (results.slice(startIndex, endIndex).length < GRID_ITEMS_PER_PAGE) {
      return;
    }

    setPage(page + 1);
  };

  const completeTableData = React.useMemo(() => {
    try {
      const completeData = Object.entries(dataByPageNumber).reduce((accumulator, [_, collection]) => {
        accumulator = [...accumulator, ...collection];
        return accumulator;
      }, []);
      return completeData;
    } catch (err) {
      console.error('Failed to parse complete dataset in TopicBuilderEditResults', err);
      return [];
    }
  }, [dataByPageNumber]);

  // Handles quick exclude button click
  const handleExcludeButtonClick = (term: string) => {
    setFilters(({ add }) => add({ key: TopicFilter.ExcludedKeywords, values: [term] }));
  };

  return (
    <Flex flexDirection="column" alignItems="flex-end">
      <EntityTable
        appTableProps={{
          onRowEnter: (_u, _w, index) => setHoveredIndex(index),
          onRowLeave: () => setHoveredIndex(null)
        }}
        isLoading={isLoading || (results?.length && !completeTableData?.length)}
        skeletonRows={20}
        shouldModifyColumnDefs={false}
        rowData={completeTableData}
        page={page}
        setPage={setPage}
        showPagination={false}
        columnDefs={[
          {
            field: 'searchTerm',
            headerName: 'Keywords',
            tableColumnProps: {
              style: {
                width: '200px'
              },
              align: 'left'
            },
            valueFormatter: (_, row: ClassifiedSearchTerm) => (
              <div style={{ width: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <TableSpan title={row.searchTerm}>{row.searchTerm}</TableSpan>
              </div>
            )
          },
          {
            field: 'totalClicks',
            headerName: 'Search Volume',
            tableColumnProps: {
              style: {
                width: '130px'
              },
              align: 'left'
            },
            valueFormatter: (_, row: ClassifiedSearchTerm) => {
              return <MetricCell metricType={METRICTYPE.VOLUME} value={row?.totalClicks} />;
            }
          },
          {
            field: 'type',
            headerName: 'Type',
            tableColumnProps: {
              style: {
                width: '70px'
              },
              align: 'left'
            }
          },
          {
            field: 'brand',
            headerName: 'Brand',
            tableColumnProps: {
              style: {
                width: '60px'
              },
              align: 'left'
            },
            valueFormatter: (_, row: ClassifiedSearchTerm) => {
              const brandName = row?.brand || 'No Brand';
              return (
                <div style={{ width: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <TableSpan title={brandName}>{brandName}</TableSpan>
                </div>
              );
            }
          },
          {
            field: 'topic',
            headerName: 'Feature',
            tableColumnProps: {
              style: {
                width: '200px'
              },
              align: 'left'
            },
            valueFormatter: () => {
              return (
                <div style={{ width: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <TableSpan title={entity?.topicName}>{entity?.topicName}</TableSpan>
                </div>
              );
            }
          },
          {
            headerName: '',
            tableColumnProps: {
              style: {
                width: '50px'
              }
            },
            valueFormatter: (_, row, index) => {
              const { searchTerm } = row;

              if (hoveredIndex === index && !readOnly) {
                return <SlCancelButton onClick={() => handleExcludeButtonClick(searchTerm)} />;
              } else {
                return null;
              }
            }
          }
        ]}
      />
      <Pagination
        currentPage={page}
        shouldUseInfinitePagination
        onClickNext={handlePageNext}
        onClickPrevious={() => setPage(Math.max(page - 1, 1))}
      />
    </Flex>
  );
};
